import { GetGroupById, IGroup, IPlayerGroup, LeaveGroup, IGroupMembers } from "@grassrootly/api";
import { GetGroupMembers } from "@grassrootly/api/providers/supabase-provider/actions";
import useBaseRequest from "../../api/BaseRequest";
import { store, useAppDispatch, useAlert } from "@grassrootly/rn-core";
import { useFocusEffect } from "@react-navigation/native";
import { useCallback, useState } from "react";
import { usePlayerGroup, useGroups } from "../../api/groups/GroupsSelector";
import { groupsActions } from "../../api/groups/GroupsReducer";
import { MainStackScreenProps } from "../../navigation/Navigation.types";

export const GROUP_TABS: { [key: string]: { alias: string; title: string } } = {
  info: { alias: "info", title: "Stats" },
  matches: { alias: "matches", title: "Matches" },
  topPlays: { alias: "topPlays", title: "Top Plays" },
  members: { alias: "members", title: "Members" },
};

interface IGroupScreenHook {
  group: IGroup | null;
  loading: boolean;
  selectedTab: string | null;
  groupTabs: Array<{ alias: string; title: string }>;
  handleNavigationPress: () => void;
  onTabSelected: (tab: string) => void;
  isEditing: boolean;
  toggleEditing: () => void;
  isOwner: boolean;
  isMember: boolean;
  canLeaveGroup: boolean;
  onAddMember: () => void;
  onNewMatch: () => void;
  onEditGroup: () => void;
  onLeaveGroup: () => void;
  onJoinGroup: () => void;
}

export function useGroupScreen({ navigation, route: { params } }: MainStackScreenProps<"group">): IGroupScreenHook {
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useState<string | null>(GROUP_TABS.info.alias);
  const [isEditing, setEditing] = useState(false);
  const [groupMembers, setGroupMembers] = useState<IGroupMembers[]>([]);
  const { warning, success } = useAlert();

  const id = params.id;
  const group = useGroups(id); // Get basic group data (always available)
  const playerGroup = usePlayerGroup(id); // Get membership data (null if not a member)
  const playerId = store.getState().auth?.user?.id || 0;
  const groupTabs = Object.values(GROUP_TABS);

  useFocusEffect(
    useCallback(() => {
      // Always refresh group data when screen is focused to ensure membership status is up to date
      execute({
        id: id,
        playerId: playerId,
      });
      // Also fetch group members to check ownership
      if (id) {
        getGroupMembers(id);
      }
    }, [id, playerId])
  );

  const { loading, execute } = useBaseRequest(GetGroupById, {
    initialPayload: {
      id,
      playerId: playerId,
    },
    autoFetch: true,
    onCompleted: (data) => {
      if (!data) return;
      const dataArray = Array.isArray(data) ? data : [data];
      dispatch(groupsActions.setGroups(dataArray));
    },
  });

  const { loading: leavingGroup, execute: leaveGroup } = useBaseRequest(LeaveGroup, {
    onCompleted: () => {
      // Remove the user's membership from the Redux store
      if (group?.id) {
        dispatch(groupsActions.removePlayerGroup(group.id));
      }
      success({ message: `You have left the group ${group?.name}` });
      // Navigate back to my-groups page
      navigation.navigate("my-groups");
    },
    onError: (error) => {
      console.error("Leave group error:", error);
      warning({ message: error.message || "Failed to leave group" });
    },
  });

  const { execute: getGroupMembers } = useBaseRequest(GetGroupMembers, {
    onCompleted: (data) => {
      if (data) {
        // Handle both array and single object responses
        const membersArray = Array.isArray(data) ? data : [data];
        setGroupMembers(membersArray);
      }
    },
  });

  const handleNavigationPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.toggleDrawer();
    }
    setEditing(false);
  };

  const onTabSelected = (alias: string) => {
    setSelectedTab(alias);
  };

  // This is not place for this feature
  const toggleEditing = () => {
    setEditing(!isEditing);
  };

  // Determine user role based on GroupPlayers entity
  const isOwner = playerGroup?.is_owner || false;
  const isMember = !!playerGroup; // If playerGroup exists, user is a member

  // Check if user can leave the group (not allowed if they are the only owner)
  const canLeaveGroup = (() => {
    if (!isMember && !isOwner) return false; // Non-members can't leave
    if (!isOwner) return true; // Non-owners can always leave
    
    // If user is owner, check if there are other owners
    // Only allow leaving if we have group members data AND there are other owners
    if (groupMembers.length === 0) return false; // Don't allow leaving if members data not loaded yet
    const ownerCount = groupMembers.filter(member => member.is_group_owner).length;
    return ownerCount > 1; // Can leave only if there are other owners
  })();

  // Menu action handlers
  const onAddMember = () => {
    if (group?.id) {
      navigation.navigate("add-player", { id: group.id, whereToAdd: "group" });
    }
  };

  const onNewMatch = () => {
    if (group?.id) {
      navigation.navigate("new-game", { groupId: group.id });
    }
  };

  const onEditGroup = () => {
    setEditing(true);
  };

  const onLeaveGroup = () => {
    if (group?.name && group?.id) {
      const confirmed = window.confirm(`Are you sure you want to leave group ${group.name}?`);
      if (confirmed) {
        console.log("Leaving group with params:", {
          groupId: group.id,
          playerId: playerId,
        });
        leaveGroup({
          groupId: group.id,
          playerId: playerId,
        });
      }
    }
  };

  const onJoinGroup = () => {
    navigation.navigate("join-group");
  };

  return { 
    group, 
    loading, 
    selectedTab, 
    groupTabs, 
    onTabSelected, 
    handleNavigationPress, 
    isEditing, 
    toggleEditing,
    isOwner,
    isMember,
    canLeaveGroup,
    onAddMember,
    onNewMatch,
    onEditGroup,
    onLeaveGroup,
    onJoinGroup
  };
}
